import { websocketLogger } from '@flipster/utils/datadog';
import { startPingPongLoop, type PingPongLoop } from '@flipster/utils/ping-pong';
import { startRttTracking, type RttTracking } from '@flipster/utils/rtt-tracking';

import Socket, { SocketPlugin, SocketPluginClass } from './Socket';
import type { MessageEvent } from './r230522/types';

export interface SocketPingPongPluginConfig {
  trackRtt?: boolean;
}

export default function SocketPingPongPlugin(config?: SocketPingPongPluginConfig): SocketPluginClass {
  const { trackRtt = false } = config ?? {};
  return class implements SocketPlugin {
    #pingPongLoop: PingPongLoop | undefined;
    #rttTracking: RttTracking | undefined;

    constructor(
      private socket: Socket,
      private sendRaw: (data: string) => void,
    ) {
      socket.on('connected', () => {
        const abortController = new AbortController();
        socket.once('closed', () => abortController.abort());

        this.#pingPongLoop = startPingPongLoop({
          abortSignal: abortController.signal,
          timeoutMs: 5000,
          waitMs: 2000,
          ping: () => {
            this.#rttTracking?.recordPing();
            this.sendRaw('ping');
          },
          timeout: () => {
            // oxlint-disable-next-line no-console
            console.warn('Socket is not responding. Reconnecting...');
            this.socket.reconnect();
          },
        });

        if (trackRtt) {
          this.#rttTracking = startRttTracking({
            abortSignal: abortController.signal,
            loggingIntervalMs: 10000, // 10초
            onRttUpdate: (rttData) => {
              websocketLogger.info('websocket_ping_rtt', {
                rtt_ms: rttData.rttMs,
                rtt_status: rttData.status,
              });

              if (process.env.NODE_ENV === 'development') {
                // oxlint-disable-next-line no-console
                console.log(`[RTT] ${rttData.status}: ${rttData.rttMs !== null ? `${rttData.rttMs}ms` : '-'}`);
              }
            },
          });
        }
      });
    }

    handleRawMessageEvent(messageEvent: MessageEvent, next: (messageEvent: MessageEvent) => void): void {
      const message = messageEvent.data;
      // ping timeout의 의도는 서버에서 메시지가 오고있는지를 확인하는 것이므로
      // pong이 아닌 메시지가 올 때에도 clear timeout 함
      this.#pingPongLoop?.clearTimeout();
      switch (message) {
        case 'pong':
          this.#pingPongLoop?.pong();
          this.#rttTracking?.recordPong();
          break;
        case 'ping':
          this.sendRaw('pong');
          break;
        default:
          next(messageEvent);
          break;
      }
    }
  };
}
